'use client';

import {
  Breakpoint,
  Breakpoints
} from '@/react/components/core-ui/Breakpoints';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import type { IContentMedia } from '../../template-data/IContentMedia';
import { baseImageWidth } from '../../utilities/getMediaWidthFromElementWidth';
import { ContentImage } from '../ContentImage';
import { ContentVideo } from '../ContentVideo';

export interface IContentMediaProps {
  /**
   * The media object from content.
   */
  media: IContentMedia;
}

/**
 * A component that renders different types of media.
 */
export const ContentMedia: FC<IContentMediaProps> = ({ media }) => {
  const componentRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(baseImageWidth);

  // Stub for image transformation
  useLayoutEffect((): void => {
    const elementWidth = componentRef.current?.clientWidth ?? baseImageWidth;
    /**
     * **Note**: There was once a bug here, where the width was being updated in a transition
     * rather than synchronously. This caused the media to occasionally render at the wrong
     * resolution due to a race condition between when the video mounted and when the transition
     * completed. Once mounted, a video instantiated with a <source> tag cannot be updated unless
     * it is remounted. So there is 2 things we do now to avoid this:
     * 1. We schedule a synchronous update for the width (see below).
     * 2. We use the video source URL as a key for the media player to force a remount when it changes.
     */
    setWidth(elementWidth);
  }, [componentRef.current?.clientWidth]); // TODO: replace this with a proper resize observer

  const defaultMedia = media?.defaultMedia ?? null;
  const tabletMedia = media?.tabletMedia ?? null;
  const desktopMedia = media?.desktopMedia ?? null;

  let defaultComponent = null;
  switch (defaultMedia?.item?.resource_type) {
    case 'image':
      defaultComponent = (
        <div style={{ height: defaultMedia.containerHeight }}>
          <ContentImage
            media={defaultMedia}
            elementWidth={width}
            highPriority={media.highPriority}
          />
        </div>
      );
      break;
    case 'video':
      defaultComponent = (
        <div style={{ height: defaultMedia.containerHeight }}>
          <ContentVideo media={defaultMedia} elementWidth={width} />
        </div>
      );
      break;
    default:
      break;
  }

  let tabletComponent = null;
  switch (tabletMedia?.item?.resource_type) {
    case 'image':
      tabletComponent = (
        <div style={{ height: tabletMedia.containerHeight }}>
          <ContentImage
            media={tabletMedia}
            elementWidth={width}
            highPriority={media.highPriority}
          />
        </div>
      );
      break;
    case 'video':
      tabletComponent = (
        <div style={{ height: tabletMedia.containerHeight }}>
          <ContentVideo media={tabletMedia} elementWidth={width} />
        </div>
      );
      break;
    default:
      break;
  }

  let desktopComponent = null;
  switch (desktopMedia?.item?.resource_type) {
    case 'image':
      desktopComponent = (
        <div style={{ height: desktopMedia.containerHeight }}>
          <ContentImage
            media={desktopMedia}
            elementWidth={width}
            highPriority={media.highPriority}
          />
        </div>
      );
      break;
    case 'video':
      desktopComponent = (
        <div style={{ height: desktopMedia.containerHeight }}>
          <ContentVideo media={desktopMedia} elementWidth={width} />
        </div>
      );
      break;
    default:
      break;
  }

  return (
    <div ref={componentRef}>
      <Breakpoints>
        <Breakpoint default>{defaultComponent ?? desktopComponent}</Breakpoint>
        <Breakpoint media="tablet">
          {tabletComponent ?? defaultComponent}
        </Breakpoint>
        <Breakpoint media="desktop">
          {desktopComponent ?? defaultComponent}
        </Breakpoint>
      </Breakpoints>
    </div>
  );
};

export default ContentMedia;
